import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchMenuItems } from '../lib/cms';

const fallbackMenu = [
  { label: 'Forside', url: '/' },
  { label: 'Om os', url: '/om-os' },
  { label: 'Ydelser', url: '/ydelser' },
  { label: 'Asfalt', url: '/ydelser/asfalt', parent_slug: 'ydelser' },
  { label: 'Rørlægning', url: '/ydelser/roerlaegning', parent_slug: 'ydelser' },
  { label: 'Entreprenørarbejde', url: '/ydelser/entreprenoerarbejde', parent_slug: 'ydelser' },
  { label: 'Mandskabsudlejning', url: '/ydelser/mandskabsudlejning', parent_slug: 'ydelser' },
  { label: 'Galleri', url: '/galleri' },
  { label: 'Kontakt', url: '/kontakt' }
];

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isYdelserOpen, setIsYdelserOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const leaveTimeoutRef = React.useRef(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    async function loadMenu() {
      const cmsMenu = await fetchMenuItems();
      if (cmsMenu && cmsMenu.length > 0) {
        setMenuItems(cmsMenu);
      } else {
        setMenuItems(fallbackMenu);
      }
    }
    loadMenu();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsYdelserOpen(false);
  }, [location]);

  const topLevelItems = menuItems.filter(item => !item.parent_slug || item.parent_slug === '');
  const ydelserSubItems = menuItems.filter(item => item.parent_slug === 'ydelser');

  const isActive = (url) => {
    if (url === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(url);
  };

  const isHome = location.pathname === '/';
  const isTransparent = false;

  return (
    <header className="w-full sticky top-0 z-40 shadow-md">
      {/* Top Row: Dark background for Logo and Call CTA */}
      <div className="bg-asphalt-950 text-white border-b border-asphalt-800/50">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/screenshot-2026-06-18-at-08-22-53-removebg-preview.png" 
              alt="JE Entreprenør Logo" 
              className="h-20 sm:h-28 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
            />
          </Link>

          {/* Call CTA on Desktop */}
          {!isMobile && (
            <a
              href="tel:+4551172145"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-safety-orange text-white hover:bg-safety-orange-hover transition-all duration-200"
            >
              <i className="fa-solid fa-phone"></i>
              <span className="whitespace-nowrap">51 17 21 45</span>
            </a>
          )}

          {/* Mobile Menu Button (under 1200px) */}
          {isMobile && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-12 w-12 items-center justify-center text-white hover:text-safety-orange transition-colors duration-200"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}></i>
            </button>
          )}
        </div>
      </div>

      {/* Bottom Row: Desktop Navigation (White background, full width) */}
      {!isMobile && (
        <div className="bg-white border-b border-asphalt-200 shadow-sm">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 py-3 w-full justify-start">
              {topLevelItems.map((item) => {
                const hasSubmenu = item.url === '/ydelser';
                const active = isActive(item.url);
                
                if (hasSubmenu) {
                  return (
                    <div
                      key={item.url}
                      className="relative"
                      onMouseEnter={() => {
                        if (leaveTimeoutRef.current) {
                          clearTimeout(leaveTimeoutRef.current);
                        }
                        setIsYdelserOpen(true);
                      }}
                      onMouseLeave={() => {
                        leaveTimeoutRef.current = setTimeout(() => {
                          setIsYdelserOpen(false);
                        }, 300); // 300ms buffer before closing
                      }}
                    >
                      <button
                        type="button"
                        className={`relative inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold transition-all duration-200 cursor-default ${
                          active
                            ? 'text-white'
                            : 'text-asphalt-950 hover:text-safety-orange'
                        }`}
                      >
                        {active && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                        <span className="relative z-10 flex items-center gap-1.5">
                          {item.label}
                          <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isYdelserOpen ? 'rotate-180' : ''}`}></i>
                        </span>
                      </button>

                      {/* Dropdown Menu */}
                      {isYdelserOpen && (
                        <div className="absolute left-0 mt-1 w-64 border border-asphalt-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200 z-50 text-asphalt-950">
                          {ydelserSubItems.map((sub) => {
                            const subActive = location.pathname === sub.url;
                            return (
                              <Link
                                key={sub.url}
                                to={sub.url}
                                className={`relative block px-4 py-2.5 text-sm font-semibold transition-all duration-150 ${
                                  subActive
                                    ? 'text-white'
                                    : 'text-asphalt-800 hover:text-safety-orange'
                                }`}
                              >
                                {subActive && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                                <span className="relative z-10 block">{sub.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    className={`relative inline-flex items-center px-5 py-2 text-sm font-bold transition-all duration-200 ${
                      active
                        ? 'text-white'
                        : 'text-asphalt-950 hover:text-safety-orange'
                    }`}
                  >
                    {active && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                    <span className="relative z-10">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Navigation Drawer (under 1200px) */}
      {isMobile && isOpen && (
        <div className="border-t border-asphalt-800 bg-asphalt-950 px-4 py-4 shadow-inner animate-in slide-in-from-top duration-300 text-white">
          <nav className="flex flex-col gap-1.5">
            {topLevelItems.map((item) => {
              const hasSubmenu = item.url === '/ydelser';
              const active = isActive(item.url);

              if (hasSubmenu) {
                return (
                  <div key={item.url} className="flex flex-col">
                    <button
                      onClick={() => setIsYdelserOpen(!isYdelserOpen)}
                      className={`relative flex items-center justify-between px-4 py-3 text-base font-bold text-left transition-all duration-200 ${
                        active
                          ? 'text-white'
                          : 'text-asphalt-200 hover:text-safety-orange'
                      }`}
                    >
                      {active && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                      <span className="relative z-10 flex items-center justify-between w-full">
                        <span>{item.label}</span>
                        <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isYdelserOpen ? 'rotate-180' : ''}`}></i>
                      </span>
                    </button>

                    {/* Mobile Submenu */}
                    {isYdelserOpen && (
                      <div className="mt-1 ml-4 border-l-2 border-asphalt-800 pl-3 flex flex-col gap-1">
                        {ydelserSubItems.map((sub) => {
                          const subActive = location.pathname === sub.url;
                          return (
                            <Link
                              key={sub.url}
                              to={sub.url}
                              className={`relative block px-4 py-2.5 text-sm font-semibold transition-all duration-150 ${
                                subActive
                                  ? 'text-white'
                                  : 'text-asphalt-300 hover:text-safety-orange'
                              }`}
                            >
                              {subActive && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                              <span className="relative z-10 block">{sub.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={`relative block px-4 py-3 text-base font-bold transition-all duration-200 ${
                    active
                      ? 'text-white'
                      : 'text-asphalt-200 hover:text-safety-orange'
                  }`}
                >
                  {active && <span className="absolute inset-0 bg-safety-orange -skew-x-12 z-0"></span>}
                  <span className="relative z-10 block">{item.label}</span>
                </Link>
              );
            })}

            {/* Mobile Call CTA */}
            <a
              href="tel:+4551172145"
              className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-safety-orange py-3.5 text-center text-base font-bold text-white hover:bg-safety-orange-hover transition-all duration-200"
            >
              <i className="fa-solid fa-phone"></i>
              <span>Ring på 51 17 21 45</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
